<template>
  <Dialog
    v-model:visible="visible"
    :header="$t('subscribeModalTitle')"
    :style="{ width: '25rem' }"
    position="bottomright"
    :draggable="false"
    pt:root:class="bg-white rounded-md m-4 relative shadow-lg overflow-hidden"
  >
    <template #container="{ closeCallback }">
      <Button
        severity="secondary"
        pt:root:class="!absolute bg-white top-4 right-4 w-[25px] h-[25px] rounded-full flex items-center justify-center bg-surface-100 border border-surface-100 hover:bg-surface-200 hover:border-surface-200"
        @click="closeCallback()"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          class="mx-0 text-[12px] sm:text-[14px] 2xl:text-[16px]"
        >
          <path
            d="M8.01186 7.00933L12.27 2.75116C12.341 2.68501 12.398 2.60524 12.4375 2.51661C12.4769 2.42798 12.4982 2.3323 12.4999 2.23529C12.5016 2.13827 12.4838 2.0419 12.4474 1.95194C12.4111 1.86197 12.357 1.78024 12.2884 1.71163C12.2198 1.64302 12.138 1.58893 12.0481 1.55259C11.9581 1.51625 11.8617 1.4984 11.7647 1.50011C11.6677 1.50182 11.572 1.52306 11.4834 1.56255C11.3948 1.60204 11.315 1.65898 11.2488 1.72997L6.99067 5.98814L2.7325 1.72997C2.59553 1.60234 2.41437 1.53286 2.22718 1.53616C2.03999 1.53946 1.8614 1.61529 1.72901 1.74767C1.59663 1.88006 1.5208 2.05865 1.5175 2.24584C1.5142 2.43303 1.58368 2.61419 1.71131 2.75116L5.96948 7.00933L1.71131 11.2675C1.576 11.403 1.5 11.5866 1.5 11.7781C1.5 11.9696 1.576 12.1532 1.71131 12.2887C1.84679 12.424 2.03043 12.5 2.2219 12.5C2.41338 12.5 2.59702 12.424 2.7325 12.2887L6.99067 8.03052L11.2488 12.2887C11.3843 12.424 11.568 12.5 11.7594 12.5C11.9509 12.5 12.1346 12.424 12.27 12.2887C12.4053 12.1532 12.4813 11.9696 12.4813 11.7781C12.4813 11.5866 12.4053 11.403 12.27 11.2675L8.01186 7.00933Z"
            fill="currentColor"
          ></path>
        </svg>
      </Button>
      
      <div class="img-holder">
        <nuxt-picture
          preload
          loading="lazy"
          :img-attrs="{ class: 'responsive-img', alt: '' }"
          src="/img/subscribe-popup.jpg"
          width="585"
          height="250"
          format="webp"
          quality="80"
          :placeholder="[100]"
          sizes=""
        />
      </div>
      <div
        ref="componentRef"
        class="p-4"
      >
        <p>
          {{ $t('subscribeModalDescr') }}
        </p>

        <Message
          v-if="(errors && errors.length) || errorCode === 401 || alreadyRegistered"
          severity="error"
          class="mb-8"
        >
          <div
            v-if="errorCode === 401"
            class="red-text"
          >
            {{ $t('errorEmail') }}
          </div>

          <div
            v-if="alreadyRegistered"
            class="red-text"
          >
            {{ $t('clientIsSubscribed') }}
          </div>

          <ul v-if="errors && errors.length">
            <li
              v-for="error, i in errors"
              :key="i"
              v-html="error"
            ></li>
          </ul>
        </Message>
      
        <div class="col">
          <FormsGenerator
            :fields="schema"
            :data="registerFormData"
            :options="{
              size: 'small'
            }"
            @return-data="(n) => registerFormData = n"
            @is-valid="(n) => validForm = n"
          />
        </div>
        
        <div class="flex justify-end gap-2 mt-8">
          <Button
            size="small"
            type="button"
            label="Cancel"
            severity="secondary"
            @click="visible = false"
          ></Button>

          <Button
            size="small"
            type="button"
            :label="$t('startSaving')"
            @click="subscribe()"
          ></Button>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import * as Yup from 'yup'

const { $i18n: i18n } = useNuxtApp()
const { alreadyRegistered, register, setAuthToken } = useCustomer()
const { useCustomCookie } = useCustomTools()
const toast = useToast()
const { drawerOptions } = usePopups()

const errors = ref(false)
const errorCode = ref(false)
const loadingForm = ref(false)
const validForm = ref(false)

const componentRef = ref(null)
const visible = ref(false)

const subscribeModalNotAllow = useCustomCookie('subscribeModalNotAllow')

/**
* Form Data
*/
const registerFormData = ref({
  email: ''
})

/*
* Form Schema
*/
const schema = ref([
  {
    label: i18n?.t('email'),
    name: 'email',
    fieldType: 'input',
    type: 'text',
    placeholder: i18n?.t('email'),
    rules: Yup.string().required(i18n?.t('errorRequiredEmail')),
    parentClasses: 's12 m12 l12 mb-0'
  }
])

/*
* Subscribe
*/
const subscribe = () => {
  errors.value = false

  const selector = componentRef.value

  startValidateForm(selector)

  validateForm(selector, '', validForm.value.yourInfo)
    .then(async () => {
      loadingForm.value = true
      await register({ email: registerFormData.value.email })
        .then((data) => {
          if (data?.status === 'error') {
            errors.value = typeof data.description === 'string' ? [data.description] : data.description
            errorCode.value = data.code
          } else if (data?.status === 'registered') {
            alreadyRegistered.value = true
          } else {
            const token = data.data.token
            setAuthToken(token).then(() => {
              errorCode.value = false
              toast.add({
                severity: 'success',
                detail: i18n?.t('accountCreated'),
                life: 5000
              })

              visible.value = false
            })
          }
        })
      loadingForm.value = false
    }).catch(() => {
      loadingForm.value = false
    })
}

/**
* Init Subscribe Modal
*/
onMounted(() => {
  useTimeoutFn(() => {
    if (!subscribeModalNotAllow.value && !drawerOptions.value.productQuote && !drawerOptions.value.conversation) {
      visible.value = true
      subscribeModalNotAllow.value = true
    }
  }, 15000)
})
</script>

<style lang="scss">
</style>
